import { type MetricWithAttribution } from 'web-vitals/attribution'
import { experiments } from '@core/experiments'

import { type TScreenName } from '../types/screenNameTypes'

import { getReleaseVersion } from './analyticsRelease'
import { buildParams, type TEventParamsRecord } from './buildParams'
import { getCurrentScreen, getScreenForPath, hasScreenForPath } from './currentScreen'
import { getDeviceInfo } from './getDeviceInfo'
import { getNetworkInfo } from './getNetworkInfo'
import { getPageType } from './getPageType'
import { enqueuePerfMetric } from './pendingPerfMetrics'
import { type TRenderType } from './renderType'
import { sendEvent } from './sendEvent'
import { isUntrackedPage } from './untrackedPage'

export type TPerfEventName = 'ttfb' | 'fcp' | 'lcp' | 'cls' | 'inp' | 'content_dom_ready'

type TPerfMetric = {
    name: TPerfEventName
    value: number
    screen: TScreenName
    renderType: TRenderType
    extraParams?: TEventParamsRecord
    isImmediate?: boolean
}

// CLS is a unitless fraction and analytics stores integers, so the perf channel keeps it x1000
const CLS_SCALE = 1000
const SUBDOMAIN = 'ac'
const MS_IN_SECOND = 1000
const SECONDS_IN_MINUTE = 60

export const sendPerfMetric = ({
    name,
    value,
    screen,
    renderType,
    extraParams = {},
    isImmediate = false,
}: TPerfMetric): void => {
    const { experimentIds, experimentVariations } = experiments.getAllExperimentsData()

    sendEvent(
        {
            name,
            payload: {
                value: Math.round(value),
                page_type: getPageType(screen),
                render_type: renderType,
                subdomain: SUBDOMAIN,
                frontend_release_version: getReleaseVersion(),
                datetime_client: Math.floor(Date.now() / MS_IN_SECOND),
                datetime_client_offset: -new Date().getTimezoneOffset() * SECONDS_IN_MINUTE,
                experiment_ids: experimentIds.join(','),
                experiment_variations: experimentVariations.join(','),
                ...getNetworkInfo(),
                ...getDeviceInfo(),
                ...buildParams(extraParams),
            },
        },
        isImmediate,
    )
}

const isNavigationTiming = (entry: PerformanceEntry): entry is PerformanceNavigationTiming =>
    entry.entryType === 'navigation'

const getServerTimingParams = (): TEventParamsRecord => {
    if (typeof performance.getEntriesByType !== 'function') {
        return {}
    }

    const [entry] = performance.getEntriesByType('navigation')

    if (entry === undefined || !isNavigationTiming(entry) || !Array.isArray(entry.serverTiming)) {
        return {}
    }

    const params: TEventParamsRecord = {}

    entry.serverTiming.forEach(timing => {
        params[timing.name] = Math.round(timing.duration)
    })

    return params
}

type TMetricName = MetricWithAttribution['name']

const EVENT_NAME_BY_METRIC: Record<TMetricName, TPerfEventName> = {
    TTFB: 'ttfb',
    FCP: 'fcp',
    LCP: 'lcp',
    CLS: 'cls',
    INP: 'inp',
}

/**
 * CLS, LCP and INP are finalised by the library on `pagehide` / `visibilitychange -> hidden`, and our
 * flush listener is registered earlier (sendEvent is imported at boot, web-vitals from an effect), so
 * it empties the batch before these land in it. They have to leave immediately or they are lost.
 */
const IMMEDIATE_METRICS: TMetricName[] = ['CLS', 'LCP', 'INP']

const isSoftNavigation = (metric: MetricWithAttribution) =>
    metric.navigationType === 'soft-navigation'

const extraParamsOf = (metric: MetricWithAttribution): TEventParamsRecord => {
    if (metric.name === 'INP') {
        return {
            input_delay: Math.round(metric.attribution.inputDelay),
            processing_duration: Math.round(metric.attribution.processingDuration),
            presentation_delay: Math.round(metric.attribution.presentationDelay),
            load_state: metric.attribution.loadState,
            interaction_type: metric.attribution.interactionType,
            interaction_target: metric.attribution.interactionTarget,
        }
    }

    // The navigation entry describes the initial document, so its Server-Timing says nothing about a
    // soft navigation — where TTFB is reported as 0 anyway.
    if (metric.name === 'TTFB' && !isSoftNavigation(metric)) {
        return getServerTimingParams()
    }

    return {}
}

const pathnameOf = (metric: MetricWithAttribution): string | null => {
    const { navigationURL } = metric

    if (navigationURL === undefined) {
        return null
    }

    return new URL(navigationURL, window.location.origin).pathname
}

export const reportWebVital = (metric: MetricWithAttribution): void => {
    const send = (screen: TScreenName, isImmediate: boolean) =>
        sendPerfMetric({
            name: EVENT_NAME_BY_METRIC[metric.name],
            value: metric.name === 'CLS' ? metric.value * CLS_SCALE : metric.value,
            screen,
            renderType: isSoftNavigation(metric) ? 'react_csr' : 'react_ssr',
            extraParams: extraParamsOf(metric),
            isImmediate,
        })

    const pathname = pathnameOf(metric)

    if (isUntrackedPage(pathname ?? window.location.pathname)) {
        return
    }

    if (pathname !== null && !hasScreenForPath(pathname)) {
        enqueuePerfMetric(pathname, screen => send(screen, true))

        return
    }

    send(
        pathname === null ? getCurrentScreen() : getScreenForPath(pathname),
        IMMEDIATE_METRICS.includes(metric.name),
    )
}
